import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAtom } from "jotai";
import { Form, FormikProvider } from "formik";
import { useFormik } from "formik";
import * as Yup from "yup";

import { LoginContext } from "../../context/login-context";
import RebatesClient from "../../RebatesClient";

import authCss from "./auth.module.scss";

import { isLoggedInAtom } from "../../components/atoms";
import FormikTextField from "../../components/form/formik-text-field/formik-text-field";
import { CustomButton } from "../../components/custom-button/custom-button";
import { Icon } from "../../components";

import ForgotPasswordForm from "./forgotpasswordform";

import { paddingXY } from "../../emotion/utils";
import css from "./login.module.css";


const Login = () => {
	const [hasError, setHasError] = useState(false);
	const navigate = useNavigate();
	const [, setIsLoggedIn] = useAtom(isLoggedInAtom);
	const [isResettingPassword, setIsResettingPassword] = useState(false);
	const { saveUserData } = useContext(LoginContext);

	function handleErrorBtn() {
		setTimeout(() => setHasError(false), 500);
	}

	const validationSchema = Yup.object({
		email: Yup.string().label("Email").required("Email is required"),
		password: Yup.string()
			.label("Password")
			.required("Password is required"),
	});

	const icon = (
		<svg width="100" height="100" viewBox="0 0 100 100">
			<circle cx="50" cy="50" r="50" fill="#D52C51" />
			<path
				d="M70 35.7143L64.2857 30L50 44.2857L35.7143 30L30 35.7143L44.2857 50L30 64.2857L35.7143 70L50 55.7143L64.2857 70L70 64.2857L55.7143 50L70 35.7143Z"
				fill="white"
			/>
		</svg>
	);

	const errorBtn = (
		<CustomButton
			label="Try Again"
			align="center"
			onClick={handleErrorBtn}
		/>
	);

	const forgotPasswordArea = (
		<ForgotPasswordForm setOpen={setIsResettingPassword} />
	);

	const errorBox = (
		<AnimatePresence>
			<motion.div
				key="errorOverlay"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0, transition: { duration: 0.5 } }}
				className={authCss.errorOverlay}
				style={{ zIndex: "2" }}
			>
				{icon}

				<div className={authCss.title}>Could not login</div>
				<div className={authCss.subtitle}>
					Wrong Username or Password
				</div>

				{errorBtn}
			</motion.div>
		</AnimatePresence>
	);

	const formik = useFormik({
		initialValues: {
			email: "",
			password: "",
		},
		validationSchema: validationSchema,
		onSubmit: async (values) => {
			const username = values["email"];
			const password = values["password"];
			const ret = await RebatesClient.login(username, password);
			if (!ret.ok) {
				setHasError(true);
				setIsLoggedIn(false);
			}

			const response = ret.body;
			setIsLoggedIn(true);
			saveUserData(response);
			navigate("/");
		},
	});

	return (
		<main className={css.main}>
			{!isResettingPassword && (
				<FormikProvider value={formik}>
					<Form className={css.form}>
						{hasError && errorBox}

            <Icon icon="ValleyLogo" />

						<div className={css.fieldset}>
              <div className={css.intro}>
							  <div className={css.title}>Log in to your account</div>
							  <div className={css.subtitle}>Welcome back! Please enter your details.</div>
              </div>

							<div className={css.fields}>
								<FormikTextField
									label="Email"
									labelShrink={true}
									name="email"
									type="email"
								/>

								<FormikTextField
									label="Password"
									labelShrink={true}
									name="password"
									type="password"
								/>

								<CustomButton
									type="submit"
									label="Sign in"
									style={{
										marginTop: 20,
										width: "100%",
									}}
								/>
								<CustomButton
									label="Forgot Password?"
									type="link"
									onClick={(e) => {
										setIsResettingPassword(true);
										e.preventDefault();
									}}
									style={{
										...paddingXY(0),
										border: 0,
										boxShadow: "none",
										margin: "0 auto",
									}}
								/>
							</div>
						</div>
					</Form>
				</FormikProvider>
			)}
			{isResettingPassword && forgotPasswordArea}
		</main>
	);
};

export default Login;
